// The location's effective settings (`settings.effective`), decoded through the registry's own schemas, and the
// pricing program every price field reads (True Margin's listed-price math lives in core).
import { useMemo } from 'react';
import { z } from 'zod';
import { settings, type SettingKey } from '@radish/contracts';
import { rate, type Rate } from '@radish/primitives';
import { useLocationQuery } from './api.tsx';

type Decoded<K extends SettingKey> = z.output<(typeof settings)[K]['schema']>;

interface EffectiveSettings {
  readonly get: <K extends SettingKey>(key: K) => Decoded<K>;
  readonly sourceOf: (key: SettingKey) => 'device' | 'location' | 'account' | 'default';
}

/** A wire value decoded with its key's schema; an absent or undecodable value is the registry default. */
export function decodeSetting<K extends SettingKey>(key: K, wire: unknown): Decoded<K> {
  const def = settings[key];
  if (wire === undefined) return def.default as Decoded<K>;
  const parsed = z.safeDecode(def.schema, wire as never);
  return (parsed.success ? parsed.data : def.default) as Decoded<K>;
}

/** The wire form of a decoded value (what `settings.set` takes). */
export function encodeSetting<K extends SettingKey>(key: K, value: Decoded<K>): unknown {
  return z.encode(settings[key].schema as z.ZodType, value as never);
}

function effectiveFrom(values: Readonly<Record<string, { readonly value: unknown; readonly source: 'device' | 'location' | 'account' | 'default' }>>): EffectiveSettings {
  return {
    get: (key) => decodeSetting(key, values[key]?.value),
    sourceOf: (key) => values[key]?.source ?? 'default',
  };
}

export const SETTINGS_QUERY = 'settings.effective';

export function useEffectiveSettings(locationId: string) {
  const query = useLocationQuery(locationId, SETTINGS_QUERY, { locationId }, (client) => client.queries.settings.effective({ locationId: locationId as never }));
  const settings = useMemo(() => (query.data === undefined ? undefined : effectiveFrom(query.data.values)), [query.data]);
  return { ...query, settings };
}

export type ProgramKind = 'traditional' | 'non_cash_adjustment' | 'true_margin';

export interface Program {
  readonly kind: ProgramKind;
  readonly cashDiscountRate: Rate;
  /** True Margin without a cash discount at checkout (list prices only). */
  readonly cashDiscountHidden: boolean;
  /** The location's default sales tax, for listed-price math on taxable lines. */
  readonly taxRate: Rate;
}

export function programOf(s: EffectiveSettings): Program {
  return {
    kind: s.get('pricing.program'),
    cashDiscountRate: s.get('pricing.cash_discount.rate'),
    cashDiscountHidden: s.get('pricing.true_margin.hide_cash_discount'),
    taxRate: s.get('pricing.tax.default_rate'),
  };
}

export const TRADITIONAL: Program = { kind: 'traditional', cashDiscountRate: rate(0n), cashDiscountHidden: false, taxRate: rate(0n) };
